import React, { useState, useEffect } from 'react';
import { 
  Plus, 
  FileText, 
  ArrowLeft,
  Package,
  FileCheck,
  BarChart3,
  Sparkles,
  Search,
  ShoppingCart,
  ArrowUpRight,
  ArrowDownLeft,
  Receipt,
  Users,
  CheckCircle2,
  AlertCircle
} from 'lucide-react';
import { motion, AnimatePresence } from 'motion/react';
import { useFirebase } from '../FirebaseProvider';
import { getRow } from '../../lib/db';
import { 
  Account, 
  AccountingAccount, 
  ManagedArticle, 
  DocumentType, 
  ManagedDocument, 
  JournalEntryLine, 
  JournalEntry,
  Transaction,
  ManagedContact
} from '../../types';
import { subscribeToArticles, subscribeToDocuments, subscribeToContacts, settleManagedDocument, updateManagedDocument, recalculateDocumentBalance, bulkRecalculateBalances } from '../../services/managementService';
import { subscribeToAccountingAccounts, groupJournalLines, deleteJournalEntry } from '../../services/accountingService';
import { subscribeToTransactions } from '../../services/financeService';
import { calculateTransactionBalances } from '../../utils/transactionBalance';
import { ArticleCatalog } from './ArticleCatalog';
import { DocumentList } from './DocumentList';
import { DocumentForm } from './DocumentForm';
import { CreditReports } from './CreditReports';
import { ManagementSmartInput } from './ManagementSmartInput';
import { ContactManager } from './ContactManager';
import { TransactionAuditReport } from './TransactionAuditReport';
import { EntryFormModal } from '../accounting/EntryFormModal';

interface ManagementDashboardProps {
  onBack: () => void;
  accounts: Account[];
}

export const ManagementDashboard: React.FC<ManagementDashboardProps> = ({ onBack, accounts: financeAccounts }) => {
  const { effectiveUserId } = useFirebase();
  const [activeTab, setActiveTab] = useState<'DOCUMENTS' | 'ARTICLES' | 'CONTACTS' | 'REPORTS' | 'AI' | 'PENDING_TX'>('DOCUMENTS');
  const [accountingAccounts, setAccountingAccounts] = useState<AccountingAccount[]>([]);
  const [articles, setArticles] = useState<ManagedArticle[]>([]);
  const [contacts, setContacts] = useState<ManagedContact[]>([]);
  const [documents, setDocuments] = useState<ManagedDocument[]>([]);
  const [transactions, setTransactions] = useState<Transaction[]>([]);
  const [loading, setLoading] = useState(true);
  const [isProcessing, setIsProcessing] = useState(false);

  const unsettledDocsCount = documents.filter(doc => !doc.journalEntryId).length;

  // Modal states
  const [showDocForm, setShowDocForm] = useState(false);
  const [docTypeForNew, setDocTypeForNew] = useState<DocumentType>('PURCHASE_INVOICE');
  const [editingDoc, setEditingDoc] = useState<ManagedDocument | null>(null);
  const [settlingDoc, setSettlingDoc] = useState<ManagedDocument | null>(null);
  const [proposedEntry, setProposedEntry] = useState<Partial<JournalEntry> | null>(null);
  const [notification, setNotification] = useState<{ message: string; type: 'success' | 'error' } | null>(null);

  useEffect(() => {
    if (notification) {
      const timer = setTimeout(() => setNotification(null), 4000);
      return () => clearTimeout(timer);
    }
  }, [notification]);

  useEffect(() => {
    if (effectiveUserId) {
      setLoading(true);
      const unsubAcc = subscribeToAccountingAccounts(effectiveUserId, (data) => {
        setAccountingAccounts(data);
        if (loading) setLoading(false);
      });
      const unsubArticles = subscribeToArticles(effectiveUserId, setArticles);
      const unsubDocs = subscribeToDocuments(effectiveUserId, setDocuments);
      const unsubContacts = subscribeToContacts(effectiveUserId, setContacts);
      const unsubTransactions = subscribeToTransactions(effectiveUserId, setTransactions);
      
      return () => {
        unsubAcc();
        unsubArticles();
        unsubDocs();
        unsubContacts();
        unsubTransactions();
      }
    }
  }, [effectiveUserId]);

  const handleDeleteJournalEntry = async (docObj: ManagedDocument) => {
    if (!effectiveUserId || !docObj.journalEntryId) return;
    try {
      await deleteJournalEntry(effectiveUserId, docObj.journalEntryId);
      await updateManagedDocument(effectiveUserId, docObj.id, { journalEntryId: null as any });
    } catch (err: any) {
      console.error("Error al eliminar asiento vinculado:", err);
      alert("Error al eliminar asiento vinculado: " + err.message);
    }
  };

  const handleSettleDocument = async (mDoc: ManagedDocument) => {
    try {
      setIsProcessing(true);
      // Simulate/Generate the entry props to show in modal
      const lines: JournalEntryLine[] = [];
      const isExpense = mDoc.type === 'PURCHASE_INVOICE' || mDoc.type === 'INTERNAL_EXPENSE';
      
      const accountsPayable = accountingAccounts.find(a => a.code === '2.1.01');
      const accountsReceivable = accountingAccounts.find(a => a.code === '1.1.03');
      const retainedEarnings = accountingAccounts.find(a => a.code === '3.3');

      if (!accountsPayable || !accountsReceivable) {
        throw new Error('Cuentas contables base no encontradas (2.1.01 / 1.1.03).');
      }

      if (mDoc.type === 'RECEIPT_EXPENSE' || mDoc.type === 'RECEIPT_INCOME') {
        const hasApplications = (mDoc.applications && mDoc.applications.length > 0) || 
                                (mDoc.appliedDocumentIds && mDoc.appliedDocumentIds.length > 0) || 
                                mDoc.parentDocumentId;
        
        if (!hasApplications) {
           throw new Error("El recibo no tiene un documento padre ni facturas vinculadas.");
        }
        
        let docCodes = '';
        if (mDoc.applications && mDoc.applications.length > 0) {
          docCodes = mDoc.applications.map(a => {
            const d = documents.find(doc => doc.id === a.documentId);
            return d ? (d.number || d.locatorCode) : '';
          }).filter(Boolean).join(', ');
        } else if (mDoc.appliedDocumentIds && mDoc.appliedDocumentIds.length > 0) {
          docCodes = mDoc.appliedDocumentIds.map(id => {
            const d = documents.find(doc => doc.id === id);
            return d ? (d.number || d.locatorCode) : '';
          }).filter(Boolean).join(', ');
        } else if (mDoc.parentDocumentId) {
          const parentDoc = documents.find(d => d.id === mDoc.parentDocumentId);
          docCodes = parentDoc ? (parentDoc.number || parentDoc.locatorCode || '') : '';
        }
        
        const isExpenseReceipt = mDoc.type === 'RECEIPT_EXPENSE';
        const reductionAccountId = isExpenseReceipt ? accountsPayable.id : accountsReceivable.id;

        // Side A: Liability/Asset reduction (Paying a liability [Debit] or Collecting an asset [Credit])
        lines.push({
          accountId: reductionAccountId,
          debit: isExpenseReceipt ? mDoc.totalAmount : 0,
          credit: isExpenseReceipt ? 0 : mDoc.totalAmount,
          description: `Cancelación Parcial/Total: ${docCodes}`
        });

        // Side B: Payment account from transactions (Paying out [Credit] or Receiving in [Debit])
        let offsetAccountId = '';
        if (mDoc.transactionIds && mDoc.transactionIds.length > 0) {
          for (const txId of mDoc.transactionIds) {
            if (offsetAccountId) break;
            const tx = transactions.find(t => t.id === txId);
            if (tx) {
              for (const detail of tx.details) {
                const linkedAcc = accountingAccounts.find(a => a.linkedFinanceAccountId === detail.accountId);
                if (linkedAcc) {
                  offsetAccountId = linkedAcc.id;
                  break;
                }
              }
            }
          }
        }
        
        if (!offsetAccountId) {
          const cashAcc = accountingAccounts.find(a => a.code.startsWith('1.1.01') && a.isSelectable);
          if (cashAcc) offsetAccountId = cashAcc.id;
        }

        lines.push({
          accountId: offsetAccountId || '',
          debit: isExpenseReceipt ? 0 : mDoc.totalAmount,
          credit: isExpenseReceipt ? mDoc.totalAmount : 0,
          description: `Pago Recibo ${mDoc.number || mDoc.locatorCode}`
        });

        setProposedEntry({
          date: mDoc.date,
          description: `Recibo de Dinero (${isExpenseReceipt ? 'EGRESO' : 'INGRESO'}) - ${mDoc.contactName} [${mDoc.number || mDoc.locatorCode}]`,
          lines,
          type: 'OPERATIVO',
          documentLocatorCode: mDoc.locatorCode
        });
        setSettlingDoc(mDoc);
        setIsProcessing(false);
        return;
      }

      if (mDoc.isHistorical) {
        if (!retainedEarnings) {
          throw new Error('Cuenta de Resultados Acumulados (3.3) no encontrada. Reinicializa el plan de cuentas.');
        }
        
        const pendingAmount = mDoc.historicalPendingAmount || 0;
        if (pendingAmount <= 1) {
          throw new Error('El saldo pendiente histórico debe ser mayor a 1 para generar un asiento.');
        }

        // Un asiento que carga el saldo inicial al pasivo/activo contra resultados acumulados
        // Si es gasto (Compra): Debe (Resultados Acumulados) - Haber (Cuentas a Pagar)
        // Si es ingreso (Venta): Debe (Cuentas a Cobrar) - Haber (Resultados Acumulados)
        lines.push({
          accountId: isExpense ? retainedEarnings.id : accountsReceivable.id,
          debit: pendingAmount,
          credit: 0,
          description: `Carga Saldo Inicial: ${mDoc.locatorCode || mDoc.number}`
        });

        lines.push({
          accountId: isExpense ? accountsPayable.id : retainedEarnings.id,
          debit: 0,
          credit: pendingAmount,
          description: `Carga Saldo Inicial: ${mDoc.locatorCode || mDoc.number}`
        });

        setProposedEntry({
          date: mDoc.date,
          description: `Asiento de Apertura Individual - ${mDoc.contactName} [${mDoc.locatorCode || mDoc.number}]`,
          lines,
          type: 'APERTURA',
          documentLocatorCode: mDoc.locatorCode,
          periodId: '' // Will be resolved by AI or system on save
        });
        setSettlingDoc(mDoc);
        return;
      }

      for (const item of mDoc.items) {
        let accountId = item.accountingAccountId || '';
        
        // Priority to the catalog's current accounting account if available
        if (item.articleId) {
          const catArticle = articles.find(a => a.id === item.articleId);
          if (catArticle && catArticle.accountingAccountId) {
            accountId = catArticle.accountingAccountId;
          }
        }

        lines.push({
          accountId: accountId,
          debit: isExpense ? item.total : 0,
          credit: isExpense ? 0 : item.total,
          description: mDoc.items.length > 1 ? `Item: ${item.total}` : ''
        });
      }

      // Offset line resolving
      let hasAllocatedTransactions = false;
      let allocatedAmount = 0;

      if (mDoc.condition === 'CONTADO' && mDoc.transactionIds && mDoc.transactionIds.length > 0) {
        const balances = calculateTransactionBalances(transactions, documents);

        for (const txId of mDoc.transactionIds) {
          const tInfo = balances[txId];
          const allocationAmount = tInfo?.allocations.find(a => a.docId === mDoc.id)?.amount || 0;
          
          if (allocationAmount > 0.01) {
            const tx = await getRow<Transaction>('transactions', txId);
            if (tx) {
              const primaryAccountId = tx.details[0]?.accountId; 
              const linkedAcc = accountingAccounts.find(a => a.linkedFinanceAccountId === primaryAccountId);
              const accId = linkedAcc ? linkedAcc.id : '';
              if (accId) {
                lines.push({
                  accountId: accId,
                  debit: isExpense ? 0 : allocationAmount,
                  credit: isExpense ? allocationAmount : 0,
                  description: `Cobro/Pago mov #${txId.substring(0, 4)}`
                });
                allocatedAmount += allocationAmount;
                hasAllocatedTransactions = true;
              }
            }
          }
        }
      }

      if (hasAllocatedTransactions) {
        const diff = mDoc.totalAmount - allocatedAmount;
        if (Math.abs(diff) > 0.01) {
          const defaultAccId = isExpense ? accountsPayable.id : accountsReceivable.id;
          lines.push({
            accountId: defaultAccId,
            debit: isExpense ? 0 : diff,
            credit: isExpense ? diff : 0
          });
        }
      } else {
        let offsetAccountId = '';
        if (mDoc.condition === 'CONTADO') {
          const cashAcc = accountingAccounts.find(a => a.code.startsWith('1.1.01') && a.isSelectable);
          if (cashAcc) offsetAccountId = cashAcc.id;
        }
        if (!offsetAccountId) {
          offsetAccountId = isExpense ? accountsPayable.id : accountsReceivable.id;
        }
        lines.push({
          accountId: offsetAccountId,
          debit: isExpense ? 0 : mDoc.totalAmount,
          credit: isExpense ? mDoc.totalAmount : 0
        });
      }

      const description = `${mDoc.type.replace('_', ' ')} - ${mDoc.contactName} - ${mDoc.number || 'SN'}`;
      
      setProposedEntry({
        date: mDoc.date,
        description,
        type: 'OPERATIVO',
        documentLocatorCode: mDoc.locatorCode,
        lines: groupJournalLines(lines)
      });
      setSettlingDoc(mDoc);
    } catch (e: any) {
      alert(e.message || "Error al preparar el asiento.");
    } finally {
      setIsProcessing(false);
    }
  };

  const handleConfirmSettle = async (entryData: any) => {
    if (!settlingDoc || !effectiveUserId) return;
    setIsProcessing(true);
    try {
      // In a real scenario we'd call a dedicated service that saves the entry AND updates the doc.
      // For now, if we use EntryFormModal, it will call createJournalEntry.
      // We need to extend EntryFormModal or handle the onSuccess here.
      // Since EntryFormModal is already built to save, I'll let it save, 
      // but I need to make sure the ManagedDocument is updated with the ID.
      // This is tricky if I can't catch the ID from EntryFormModal.
      
      // Let's use settleManagedDocument instead for a clean atomic process.
      await settleManagedDocument(effectiveUserId, settlingDoc, accountingAccounts);
      setNotification({ message: "Documento asentado correctamente.", type: 'success' });
      setSettlingDoc(null);
      setProposedEntry(null);
    } catch (e: any) {
      setNotification({ message: "Error al asentar documento: " + e.message, type: 'error' });
    } finally {
      setIsProcessing(false);
    }
  };

  const handleRecalculateBalance = async (docId: string) => {
    if (!effectiveUserId) return;
    try {
      await recalculateDocumentBalance(effectiveUserId, docId);
      setNotification({ message: "Saldo recalculado.", type: 'success' });
    } catch (e: any) {
      setNotification({ message: "Error al recalcular saldo: " + e.message, type: 'error' });
    }
  };

  const handleRecalculateAll = async () => {
    if (!effectiveUserId) return;
    try {
      setIsProcessing(true);
      await bulkRecalculateBalances(effectiveUserId);
      setNotification({ message: "Recálculo global completado con éxito.", type: 'success' });
    } catch (err: any) {
      console.error(err);
      setNotification({ message: "Error en recálculo global: " + err.message, type: 'error' });
    } finally {
      setIsProcessing(false);
    }
  };

  if (loading && accountingAccounts.length === 0) {
    return (
      <div className="flex items-center justify-center p-20">
        <Sparkles className="animate-spin text-emerald-500" size={32} />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {isProcessing && (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm z-[200] flex flex-col items-center justify-center text-white">
          <div className="animate-spin rounded-full h-12 w-12 border-b-4 border-emerald-500 mb-4"></div>
          <span className="font-bold tracking-wider animate-pulse">Procesando...</span>
        </div>
      )}
      <header className="flex flex-col md:flex-row md:items-center justify-between gap-4">
        <div className="flex items-center gap-4">
          <button 
            onClick={onBack}
            className="p-2 hover:bg-zinc-800 rounded-xl transition-colors text-zinc-400 hover:text-white"
          >
            <ArrowLeft size={20} />
          </button>
          <div>
            <h1 className="text-2xl font-bold text-white flex items-center gap-2">
              Operaciones de Gestión
              <span className="text-[10px] bg-emerald-500/20 text-emerald-400 px-2 py-0.5 rounded-full font-bold uppercase tracking-widest border border-emerald-500/30">
                Nuevo
              </span>
            </h1>
            <p className="text-sm text-zinc-500">Documentación comercial, conciliación y catálogo de artículos.</p>
          </div>
        </div>
      </header>

      <nav className="flex gap-1 bg-zinc-900 p-1.5 rounded-2xl border border-zinc-800 overflow-x-auto scrolbar-hide">
        <button 
          onClick={() => setActiveTab('DOCUMENTS')}
          className={`relative flex items-center gap-2 px-6 py-2.5 rounded-xl font-bold text-sm transition-all whitespace-nowrap ${
            activeTab === 'DOCUMENTS' ? 'bg-zinc-800 text-white' : 'text-zinc-500 hover:text-zinc-300'
          }`}
        >
          <FileText size={18} />
          Documentos
          {unsettledDocsCount > 0 && (
            <span className="flex items-center justify-center bg-amber-500 text-black text-[10px] px-1.5 h-5 min-w-[20px] font-black rounded-full shadow-sm animate-pulse">
              {unsettledDocsCount}
            </span>
          )}
        </button>
        <button 
          onClick={() => setActiveTab('CONTACTS')}
          className={`flex items-center gap-2 px-6 py-2.5 rounded-xl font-bold text-sm transition-all whitespace-nowrap ${
            activeTab === 'CONTACTS' ? 'bg-zinc-800 text-white' : 'text-zinc-500 hover:text-zinc-300'
          }`}
        >
          <Users size={18} />
          Contactos
        </button>
        <button 
          onClick={() => setActiveTab('ARTICLES')}
          className={`flex items-center gap-2 px-6 py-2.5 rounded-xl font-bold text-sm transition-all whitespace-nowrap ${
            activeTab === 'ARTICLES' ? 'bg-zinc-800 text-white' : 'text-zinc-500 hover:text-zinc-300'
          }`}
        >
          <Package size={18} />
          Artículos
        </button>
        <button 
          onClick={() => setActiveTab('REPORTS')}
          className={`flex items-center gap-2 px-6 py-2.5 rounded-xl font-bold text-sm transition-all whitespace-nowrap ${
            activeTab === 'REPORTS' ? 'bg-zinc-800 text-white' : 'text-zinc-500 hover:text-zinc-300'
          }`}
        >
          <BarChart3 size={18} />
          Informes Crédito
        </button>
        <button 
          onClick={() => setActiveTab('PENDING_TX')}
          className={`flex items-center gap-2 px-6 py-2.5 rounded-xl font-bold text-sm transition-all whitespace-nowrap ${
            activeTab === 'PENDING_TX' ? 'bg-amber-500/10 text-amber-500' : 'text-zinc-500 hover:text-zinc-300'
          }`}
        >
          <FileCheck size={18} />
          Auditoría de Movimientos
        </button>
        <button 
          onClick={() => setActiveTab('AI')}
          className={`flex items-center gap-2 px-6 py-2.5 rounded-xl font-bold text-sm transition-all whitespace-nowrap ${
            activeTab === 'AI' ? 'bg-indigo-500/10 text-indigo-400' : 'text-zinc-500 hover:text-zinc-300'
          }`}
        >
          <Sparkles size={18} />
          Entrada Inteligente
        </button>
      </nav>

      <main>
        <AnimatePresence mode="wait">
          {activeTab === 'DOCUMENTS' && (
            <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -10 }}>
              <DocumentList 
                documents={documents}
                onAdd={(type) => {
                  setDocTypeForNew(type);
                  setEditingDoc(null);
                  setShowDocForm(true);
                }}
                onEdit={(doc) => {
                   setEditingDoc(doc);
                   setDocTypeForNew(doc.type);
                   setShowDocForm(true);
                }}
                onUpdateImage={async (docId, baseDocumentUrl) => {
                  if (!effectiveUserId) return;
                  try {
                    await updateManagedDocument(effectiveUserId, docId, { baseDocumentUrl });
                  } catch (error) {
                    console.error("Error updating document image:", error);
                    alert("Error al actualizar la imagen del documento.");
                  }
                }}
                onSettle={handleSettleDocument}
                onDeleteJournalEntry={handleDeleteJournalEntry}
                onRecalculateBalance={handleRecalculateBalance}
                onRecalculateAll={handleRecalculateAll}
              />
            </motion.div>
          )}
          {activeTab === 'ARTICLES' && (
            <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -10 }}>
              <ArticleCatalog userId={effectiveUserId!} accountingAccounts={accountingAccounts} />
            </motion.div>
          )}
          {activeTab === 'CONTACTS' && (
            <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -10 }}>
              <ContactManager userId={effectiveUserId!} />
            </motion.div>
          )}
          {activeTab === 'REPORTS' && (
            <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -10 }}>
              <CreditReports documents={documents} />
            </motion.div>
          )}
          {activeTab === 'AI' && (
            <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -10 }}>
              <ManagementSmartInput 
                articles={articles}
                contacts={contacts}
                accountingAccounts={accountingAccounts}
                transactions={transactions}
                documents={documents}
                accounts={financeAccounts}
                onSuccess={() => {
                  // Realizamos el refresco de los datos pero no cambiamos de pestaña automáticamente
                  // para que el usuario pueda ver el mensaje de confirmación y los códigos generados.
                }}
              />
            </motion.div>
          )}
          {activeTab === 'PENDING_TX' && (
            <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -10 }}>
              <TransactionAuditReport userId={effectiveUserId!} documents={documents} transactions={transactions} />
            </motion.div>
          )}
        </AnimatePresence>
      </main>

      <AnimatePresence>
        {showDocForm && (
          <DocumentForm 
            userId={effectiveUserId!}
            initialType={docTypeForNew}
            editingDocument={editingDoc}
            articles={articles}
            accounts={financeAccounts}
            onClose={() => setShowDocForm(false)}
            onSuccess={() => setShowDocForm(false)}
          />
        )}
        {settlingDoc && (
          <EntryFormModal 
            userId={effectiveUserId!}
            accounts={accountingAccounts}
            onClose={() => {
              setSettlingDoc(null);
              setProposedEntry(null);
            }}
            initialData={proposedEntry as any}
            onSuccess={(entryId) => {
              // The EntryFormModal will call createJournalEntry.
              // We need to link the doc to this entryId.
              // Note: I'll need to update EntryFormModal to accept initialData and onSuccess.
              setSettlingDoc(null);
              setProposedEntry(null);
            }}
            linkedDocId={settlingDoc.id}
          />
        )}
      </AnimatePresence>

      <AnimatePresence>
        {notification && (
          <motion.div
            initial={{ opacity: 0, y: 50, scale: 0.9 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, scale: 0.9, y: 20 }}
            className={`fixed bottom-8 left-1/2 -translate-x-1/2 z-[100] px-6 py-3 rounded-2xl border shadow-2xl flex items-center gap-3 font-bold text-sm backdrop-blur-md ${
              notification.type === 'success' 
                ? 'bg-emerald-500/90 border-emerald-400 text-black' 
                : 'bg-red-500/90 border-red-400 text-white'
            }`}
          >
            {notification.type === 'success' ? <CheckCircle2 size={18} /> : <AlertCircle size={18} />}
            {notification.message}
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};
